import {isNil, isString} from "lodash"

import {CurrencyType} from "@irati/payments/enums"
import authentication from "@irati/auth"

import {PaymentCurrencyType} from "@irati/types"
import {resolveRewardPointsName} from "../rewardPoints/resolveRewardPointsName"
import getDeviceLocale from "../lang/helpers/getDeviceLocale"
import {DEFAULT_LANG} from "../lang/helpers/getSupportedLocale"

interface FormatCurrency {
  isCents?: boolean
  value?: number | string
  currencyCode?: CurrencyType | string
  locale?: string
  trimZerosFromWholeNumber?: boolean // should round £10.00 to £10. Default is true
  rewardPointsName?: string
}

export const formatCurrency = ({
  isCents = true,
  value,
  currencyCode = authentication.defaultUserCurrency(),
  locale = getDeviceLocale(),
  trimZerosFromWholeNumber = true,
  rewardPointsName
}: FormatCurrency) => {
  if (isNil(value)) {
    const symbol = formatForLocale(currencyCode, locale, 1, trimZerosFromWholeNumber, rewardPointsName)
    return symbol.replace(/[0-9,. ]/g, "")
  }
  const normalisedValue = getNormalisedValue(isCents, currencyCode, value)

  return formatForLocale(currencyCode, locale, normalisedValue, trimZerosFromWholeNumber, rewardPointsName)
}

/**
 * @description: Turn a cent currency value into the full currency value
 */
export function getNormalisedValue(isCents: boolean, currency: string, value: string | number) {
  if (isCents) {
    switch (currency) {
      // add non-cent based currencies here
      default:
        return parseValue(value) / 100
    }
  }

  return parseValue(value) || 0
}

/**
 * @description: Turn a float currency value to a basic value (cent)
 */
export function getCentValue(currency: string, value: string | number) {
  switch (currency) {
    // add non-cent based currencies here
    default:
      return Math.round(parseValue(value) * 100)
  }
}

function parseValue(value: string | number) {
  if (isString(value)) {
    return parseFloat(value)
  }
  return value
}

function formatForLocale(
  currency: string | PaymentCurrencyType,
  locale: string,
  value: number | string,
  trimZerosFromWholeNumber: boolean,
  rewardPointsName?: string
) {
  const format = currency ? {style: "currency", currency, localeMatcher: "best fit"} : undefined
  let text = ""
  switch (currency) {
    case PaymentCurrencyType.Tt:
      text = `${value} Flexi Points`
      break
    case PaymentCurrencyType.Ep:
    case PaymentCurrencyType.Rp:
      text = `${value} ${resolveRewardPointsName(rewardPointsName ?? authentication.business()?.rewardPointsName)}`
      break
    default:
      try {
        text = new Intl.NumberFormat(locale, format).format(value)
      } catch (error) {
        text = new Intl.NumberFormat(DEFAULT_LANG, format).format(value)
      }
  }
  if (trimZerosFromWholeNumber) {
    text = text.replace(/[,.]00([ ].)?$/, "$1")
  }
  return text
}
